/* eslint-disable react-refresh/only-export-components */
/**
 * JobsContext — 生成キュー (activeJobs) と集約通知バッチ。
 *
 * 他の 3 Context に一切依存しない独立ブロック。消費者も
 * GenerationQueueWindow / AppLayout / ストーリーボード系フックなど少数のため、
 * 分離することでポーリング中の 1 秒おきの activeJobs 更新が
 * 設定フォームや素材パネルへ波及しなくなる。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { waitForJobCompletion } from '../../../services/asyncJobApi';
import type { FacePrivacyFlaggedItem } from '../../../services/asyncJobApi';
import { useToast } from '../../../hooks/useToast';
import { notifyJobComplete } from '../../../utils/jobNotifications';
import type { ActiveJob, StoryboardNotifyBatch } from './types';

/**
 * Seedance 実在人物フィルタで動画生成が失敗したときの原因素材ハイライト状態。
 * useVideoHandlers がジョブ失敗時にセットし、StoryboardCandidates (チェックボックス・
 * 絵コンテカードの赤枠) と SceneVideoBox (原因一覧バナー + 覆面化再生成) が読む。
 * 同じシナリオで次の動画生成を開始したときにクリアされる。
 */
export type FacePrivacyFlaggedState = {
  jobId: string;
  /** scene モード時の対象シナリオ ID (プロンプトモードは null) */
  scenarioId: string | null;
  /** scene モード時のシーン key (`${scenarioId}-scene-${order}`) */
  sceneKey: string | null;
  items: FacePrivacyFlaggedItem[];
};

const STORAGE_KEY_ACTIVE_JOBS = 'generation_active_jobs';

/** sessionStorageからアクティブジョブを復元 */
function getInitialActiveJobs(): ActiveJob[] {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY_ACTIVE_JOBS);
    if (saved) {
      return JSON.parse(saved) as ActiveJob[];
    }
  } catch {
    // パースエラーは無視
  }
  return [];
}

export type JobsContextValue = {
  // 生成キュー状態
  activeJobs: ActiveJob[];
  setActiveJobs: Dispatch<SetStateAction<ActiveJob[]>>;
  showJobQueueModal: boolean;
  setShowJobQueueModal: Dispatch<SetStateAction<boolean>>;
  // 絵コンテ自動生成の集約通知バッチ操作。詳細は StoryboardNotifyBatch 参照。
  openStoryboardBatch: () => string;
  addStoryboardBatchSlot: (batchId: string) => void;
  resolveStoryboardBatchSlot: (batchId: string) => void;
  closeStoryboardBatch: (batchId: string) => void;
  /** シナリオ生成を切り離したときのバッチ後始末 (詳細は実装のコメント)。 */
  discardStoryboardBatch: (batchId: string) => void;
  // フレーム一括生成の集約通知バッチ操作。ループ開始時に open してその ID を各
  // フレームジョブの notifyBatchId に渡し、完了時に close する。
  openFrameNotifyBatch: () => string;
  closeFrameNotifyBatch: (batchId: string) => void;
  // Seedance 実在人物フィルタの原因素材ハイライト (詳細は FacePrivacyFlaggedState)
  facePrivacyFlagged: FacePrivacyFlaggedState | null;
  setFacePrivacyFlagged: Dispatch<SetStateAction<FacePrivacyFlaggedState | null>>;
  // このセッションで覆面化 (静止画作成+永続紐付け) 済みの素材 ID。
  // SelectedAssetsPanel がバッジ表示 (顔を含む → 覆面化済み) に使う。
  facePrivacySanitizedIds: string[];
  addFacePrivacySanitizedId: (materialId: string) => void;
};

const JobsContext = createContext<JobsContextValue | null>(null);

// リロード後に復帰したポーリングが完了したときのトースト文言 (種別別。未登録は画像扱い)
const RESUME_COMPLETED_TOAST: Record<string, string> = {
  video: '動画の生成が完了しました',
  'scene-preview': 'シーン画像の生成が完了しました',
  'video-edit-propose': 'AI編集の提案が完了しました。動画エディタを開くと確認できます',
  'video-edit-analyze': 'シーンの解析が完了しました',
  'video-edit-render': 'MP4の書き出しが完了しました',
};

export function GenerationJobsProvider({ children }: { children: ReactNode }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  // 生成キュー状態（ページ遷移・リロードで永続化するためここで管理）
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>(getInitialActiveJobs);
  const [showJobQueueModal, setShowJobQueueModal] = useState(false);

  // Seedance 実在人物フィルタの原因素材ハイライト。リロード永続化はしない
  // (失敗トースト/エラーバナーが再案内するため揮発で十分)。
  const [facePrivacyFlagged, setFacePrivacyFlagged] = useState<FacePrivacyFlaggedState | null>(null);
  // 覆面化済み素材 ID (セッション揮発)。以降の Seedance 生成はバックエンドが
  // 永続紐付けを見て自動で差し替えるため、FE はバッジ表示にのみ使う。
  const [facePrivacySanitizedIds, setFacePrivacySanitizedIds] = useState<string[]>([]);
  const addFacePrivacySanitizedId = useCallback((materialId: string) => {
    setFacePrivacySanitizedIds((prev) => (
      prev.includes(materialId) ? prev : [...prev, materialId]
    ));
  }, []);

  // 絵コンテ自動生成の集約通知バッチ。リロードを跨いだ永続化はしない
  // (消失時は notifyBatchId 付きジョブを個別通知へフォールバックする)。
  const [storyboardBatches, setStoryboardBatches] = useState<Record<string, StoryboardNotifyBatch>>({});
  const storyboardBatchSeqRef = useRef(0);
  const openStoryboardBatch = useCallback(() => {
    storyboardBatchSeqRef.current += 1;
    const uniqueSuffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const id = `sb-batch-${storyboardBatchSeqRef.current}-${uniqueSuffix}`;
    setStoryboardBatches((prev) => ({ ...prev, [id]: { id, pending: 0, total: 0, closed: false } }));
    return id;
  }, []);
  const addStoryboardBatchSlot = useCallback((batchId: string) => {
    setStoryboardBatches((prev) => {
      const b = prev[batchId];
      if (!b) return prev;
      return { ...prev, [batchId]: { ...b, pending: b.pending + 1, total: b.total + 1 } };
    });
  }, []);
  const resolveStoryboardBatchSlot = useCallback((batchId: string) => {
    setStoryboardBatches((prev) => {
      const b = prev[batchId];
      if (!b) return prev;
      return { ...prev, [batchId]: { ...b, pending: Math.max(0, b.pending - 1) } };
    });
  }, []);
  const closeStoryboardBatch = useCallback((batchId: string) => {
    setStoryboardBatches((prev) => {
      const b = prev[batchId];
      if (!b) return prev;
      return { ...prev, [batchId]: { ...b, closed: true } };
    });
  }, []);
  // シナリオ生成を生成キューの ✕ で切り離したときのバッチ後始末
  // (docs/features/scenario-generation-detach-plan.md §4.4)。
  //  - スロットが 1 件以上積まれている (total > 0) = ready 済みシェルの絵コンテジョブが
  //    既に走っている → 通常どおり close し、全確定時の集約通知に任せる
  //  - スロットが 0 件 (total === 0) → バッチ自体を削除する。close すると total===0 の
  //    フォールバックで「シナリオ生成完了」通知が切り離した時点で誤発火するため。
  // total は Provider 内の state にしか無いので、この判定はここで行う。
  const discardStoryboardBatch = useCallback((batchId: string) => {
    setStoryboardBatches((prev) => {
      const b = prev[batchId];
      if (!b) return prev;
      if (b.total > 0) return { ...prev, [batchId]: { ...b, closed: true } };
      const next = { ...prev };
      delete next[batchId];
      return next;
    });
  }, []);

  // フレーム一括生成の集約通知バッチ。絵コンテと違い一括生成ループが直列 await で
  // 「全完了」を自分で検知できるため、スロット計数は持たず「生存中の batchId 集合」
  // のみ管理する。ループ開始時に open、完了時 (成功/中断/キャンセル問わず) に close。
  // リロードを跨いだ永続化はしない (消失時は notifyBatchId 付きジョブを個別通知へ
  // フォールバックさせる)。まとめ通知はループ側が完了時に 1 本だけ出す。
  const [frameNotifyBatchIds, setFrameNotifyBatchIds] = useState<Set<string>>(new Set());
  const frameNotifyBatchSeqRef = useRef(0);
  const openFrameNotifyBatch = useCallback(() => {
    frameNotifyBatchSeqRef.current += 1;
    const uniqueSuffix = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const id = `frame-batch-${frameNotifyBatchSeqRef.current}-${uniqueSuffix}`;
    setFrameNotifyBatchIds((prev) => new Set(prev).add(id));
    return id;
  }, []);
  const closeFrameNotifyBatch = useCallback((batchId: string) => {
    setFrameNotifyBatchIds((prev) => {
      if (!prev.has(batchId)) return prev;
      const next = new Set(prev);
      next.delete(batchId);
      return next;
    });
  }, []);

  // activeJobsをsessionStorageに同期（リロード時の復元用）
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY_ACTIVE_JOBS, JSON.stringify(activeJobs));
    } catch {
      // 書き込みエラーは無視
    }
  }, [activeJobs]);

  // ジョブ完了のブラウザ通知（集約点）。
  // 各完了コールバックに散らさず、activeJobs の polling → completed 遷移を
  // ここで一括監視して通知する。これにより image/video/scene-preview/
  // concat-video/storyboard/ブランドカット/リロード復帰がまとめてカバーされる。
  // notifiedJobIds で一度通知した jobId の再通知（再レンダリング・復元）を抑止。
  const notifiedJobIdsRef = useRef<Set<string>>(new Set());
  const notifyInitializedRef = useRef(false);
  useEffect(() => {
    // 初回（リロード・ブラウザ復元直後）は、復元時点で既に completed だった
    // ジョブを「通知済み」として投入するだけで通知しない。これにより
    // 「このセッション中に polling → completed へ遷移した」完了のみ通知する。
    if (!notifyInitializedRef.current) {
      notifyInitializedRef.current = true;
      for (const job of activeJobs) {
        if (job.status === 'completed') notifiedJobIdsRef.current.add(job.jobId);
      }
      return;
    }
    for (const job of activeJobs) {
      if (job.status !== 'completed') continue;
      if (notifiedJobIdsRef.current.has(job.jobId)) continue;
      notifiedJobIdsRef.current.add(job.jobId);
      // 集約通知バッチ (絵コンテ自動生成 / フレーム一括生成) に属するジョブは個別通知せず、
      // バッチ完了時にまとめて 1 本だけ通知する。バッチが既に存在しない場合
      // (リロードで消失) は従来どおり個別通知へフォールバックする。
      if (job.notifyBatchId && (storyboardBatches[job.notifyBatchId] || frameNotifyBatchIds.has(job.notifyBatchId))) continue;
      notifyJobComplete({ type: job.type, body: job.label, jobId: job.jobId });
    }
  }, [activeJobs, storyboardBatches, frameNotifyBatchIds]);

  // 絵コンテ自動生成バッチの完了通知。全シナリオの全絵コンテが確定 (成功/失敗問わず)
  // した時点で 1 本だけ通知する。closed かつ pending===0 で完了とみなす。
  const notifiedBatchIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const batch of Object.values(storyboardBatches)) {
      if (!batch.closed || batch.pending > 0) continue;
      if (notifiedBatchIdsRef.current.has(batch.id)) continue;
      notifiedBatchIdsRef.current.add(batch.id);
      if (batch.total > 0) {
        // 全絵コンテ完了 → 既存の絵コンテ文言を流用。
        notifyJobComplete({ type: 'storyboard', jobId: batch.id });
      } else {
        // 絵コンテジョブが 1 本も走らなかった (絵コンテモードのシナリオが無かった等)
        // → 抑止したシナリオ完了通知をフォールバックで出し「無通知」を防ぐ。
        notifyJobComplete({ type: 'scenario', jobId: batch.id });
      }
    }
  }, [storyboardBatches]);

  // リロード後にpolling中だったジョブのポーリングを再開
  const recoveredRef = useRef(false);
  useEffect(() => {
    if (recoveredRef.current) return;
    recoveredRef.current = true;

    // シナリオ本体ジョブはクライアント生成 ID (scenario-...) でキューに積まれ、
    // 実ジョブの復帰は useScenarioGeneration 側 (pollScenarioJob) が別系統で行う。
    // 汎用の waitForJobCompletion に渡すと実在しない job_id として失敗扱いになるため除外する。
    const pollingJobs = activeJobs.filter(j => j.status === 'polling' && j.type !== 'scenario');
    if (pollingJobs.length === 0) return;

    // リロード後はステータスバーのみ表示（キューウィンドウは開かない）

    for (const job of pollingJobs) {
      waitForJobCompletion(job.jobId, {
        maxDuration: 10 * 60 * 1000,
        // 別タブ・最小化中でも完了を検知してブラウザ通知を出すため hidden 中も継続
        skipVisibilityCheck: true,
      })
        .then((asyncJob) => {
          const resultData = asyncJob.result_data as Record<string, unknown>;
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job.jobId ? {
              ...j,
              status: 'completed' as const,
              result: {
                id: (resultData.job_id as string) || job.jobId,
                material_id: (resultData.material_id as string) || undefined,
                image_url: (resultData.image_url as string) || undefined,
              },
            } : j
          ));
          queryClient.invalidateQueries({ queryKey: ['generation', 'jobs'] });
          queryClient.invalidateQueries({ queryKey: ['temporary-materials'] });
          queryClient.invalidateQueries({ queryKey: ['credits'] });
          toast.success(RESUME_COMPLETED_TOAST[job.type ?? ''] ?? '画像の生成が完了しました');
        })
        .catch((error) => {
          const errorMsg = error instanceof Error ? error.message : String(error);
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job.jobId ? { ...j, status: 'failed' as const, error: errorMsg } : j
          ));
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<JobsContextValue>(() => ({
    activeJobs,
    setActiveJobs,
    showJobQueueModal,
    setShowJobQueueModal,
    openStoryboardBatch,
    addStoryboardBatchSlot,
    resolveStoryboardBatchSlot,
    closeStoryboardBatch,
    discardStoryboardBatch,
    openFrameNotifyBatch,
    closeFrameNotifyBatch,
    facePrivacyFlagged,
    setFacePrivacyFlagged,
    facePrivacySanitizedIds,
    addFacePrivacySanitizedId,
  }), [
    activeJobs, showJobQueueModal,
    openStoryboardBatch, addStoryboardBatchSlot, resolveStoryboardBatchSlot,
    closeStoryboardBatch, discardStoryboardBatch, openFrameNotifyBatch, closeFrameNotifyBatch,
    facePrivacyFlagged, facePrivacySanitizedIds, addFacePrivacySanitizedId,
  ]);

  return <JobsContext.Provider value={value}>{children}</JobsContext.Provider>;
}

/** 生成キューだけを購読する。設定 / 素材 / 属性ツリーの更新では再レンダーしない。 */
export function useGenerationJobs(): JobsContextValue {
  const ctx = useContext(JobsContext);
  if (!ctx) {
    throw new Error('useGenerationJobs must be used within GenerationProvider');
  }
  return ctx;
}

/** Provider 外では null を返す版 (合成ファサード用)。 */
export function useGenerationJobsSafe(): JobsContextValue | null {
  return useContext(JobsContext);
}
